import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Loader2, User, UserCog, Briefcase, Building2, Command as CommandIcon, X } from 'lucide-react';
import { Input } from './ui/input';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Internal team-member roles. Used for the role-filter chip row.
const ROLE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'recruiter', label: 'Recruiter' },
  { value: 'team_lead', label: 'Team Lead' },
  { value: 'delivery_manager', label: 'Delivery Mgr' },
  { value: 'admin', label: 'Admin' },
];

// 200ms is the sweet spot — fast enough to feel live, slow enough to skip
// keystrokes mid-typing.
const DEBOUNCE_MS = 200;

const GlobalSearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ candidates: [], users: [], jobs: [], clients: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [roleFilter, setRoleFilter] = useState(''); // '' | 'recruiter' | ...
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'candidates' | 'users' | 'jobs' | 'clients'
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Flatten results into a single array for keyboard navigation. Order:
  // candidates → jobs → clients → users. Section dividers are not keyboard-stops.
  const flatResults = useMemo(() => {
    const filteredUsers = roleFilter
      ? results.users.filter((u) => u.role === roleFilter)
      : results.users;
    const showCands = typeFilter === 'all' || typeFilter === 'candidates';
    const showUsers = typeFilter === 'all' || typeFilter === 'users';
    const showJobs = typeFilter === 'all' || typeFilter === 'jobs';
    const showClients = typeFilter === 'all' || typeFilter === 'clients';
    const cands = showCands ? results.candidates : [];
    const jobs = showJobs ? results.jobs : [];
    const clients = showClients ? results.clients : [];
    const users = showUsers ? filteredUsers : [];
    return [...cands, ...jobs, ...clients, ...users];
  }, [results, roleFilter, typeFilter]);

  // Cmd/Ctrl + K opens & focuses the search globally.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        // requestAnimationFrame ensures the input is mounted before focus
        requestAnimationFrame(() => inputRef.current?.focus());
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Click-away to close.
  useEffect(() => {
    const onClick = (e) => {
      if (open && containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Debounced fetch on every q change.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setResults({ candidates: [], users: [], jobs: [], clients: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_BASE}/search/global`, {
          params: { q: q.trim(), limit: 6 },
        });
        setResults({
          candidates: res?.data?.candidates || [],
          users: res?.data?.users || [],
          jobs: res?.data?.jobs || [],
          clients: res?.data?.clients || [],
        });
        setActiveIndex(-1);
      } catch {
        setResults({ candidates: [], users: [], jobs: [], clients: [] });
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const handleSelect = useCallback(
    (item) => {
      if (!item?.link) return;
      setOpen(false);
      setQ('');
      setResults({ candidates: [], users: [], jobs: [], clients: [] });
      navigate(item.link);
    },
    [navigate]
  );

  const onKeyDownInput = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(flatResults.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(-1, i - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < flatResults.length) {
        e.preventDefault();
        handleSelect(flatResults[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const filteredUsers = roleFilter
    ? results.users.filter((u) => u.role === roleFilter)
    : results.users;
  const showCands = typeFilter === 'all' || typeFilter === 'candidates';
  const showUsers = typeFilter === 'all' || typeFilter === 'users';
  const showJobs = typeFilter === 'all' || typeFilter === 'jobs';
  const showClients = typeFilter === 'all' || typeFilter === 'clients';
  const visibleCands = showCands ? results.candidates : [];
  const visibleUsers = showUsers ? filteredUsers : [];
  const visibleJobs = showJobs ? results.jobs : [];
  const visibleClients = showClients ? results.clients : [];
  const totalVisible =
    visibleCands.length + visibleJobs.length + visibleClients.length + visibleUsers.length;

  const showDropdown =
    open && (q.trim().length >= 2 || loading || totalVisible > 0);

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-md"
      data-testid="global-search-container"
    >
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDownInput}
          placeholder="Search candidates, team members…"
          className="pl-9 pr-16 h-9 bg-white/70 border-slate-200 focus-visible:bg-white focus-visible:border-teal-300 focus-visible:ring-1 focus-visible:ring-teal-300"
          data-testid="global-search-input"
          aria-label="Global search"
          aria-autocomplete="list"
          aria-controls="global-search-listbox"
          aria-expanded={showDropdown}
        />
        {q ? (
          <button
            type="button"
            onClick={() => {
              setQ('');
              setResults({ candidates: [], users: [], jobs: [], clients: [] });
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Clear search"
            data-testid="global-search-clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <span
            className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[10px] text-slate-400 font-medium"
            aria-hidden="true"
            title="Press ⌘K (or Ctrl+K) to focus search"
          >
            <CommandIcon className="w-3 h-3" />K
          </span>
        )}
      </div>

      {showDropdown && (
        <div
          id="global-search-listbox"
          role="listbox"
          className="absolute right-0 left-0 top-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50 max-h-[70vh] overflow-y-auto"
          data-testid="global-search-dropdown"
        >
          {/* Filter chips */}
          <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-100 bg-slate-50/60">
            <FilterChip
              label="All"
              active={typeFilter === 'all'}
              onClick={() => setTypeFilter('all')}
              testId="global-search-filter-all"
            />
            <FilterChip
              label="Candidates"
              active={typeFilter === 'candidates'}
              onClick={() => setTypeFilter('candidates')}
              testId="global-search-filter-candidates"
              count={results.candidates.length}
            />
            <FilterChip
              label="Jobs"
              active={typeFilter === 'jobs'}
              onClick={() => setTypeFilter('jobs')}
              testId="global-search-filter-jobs"
              count={results.jobs.length}
            />
            <FilterChip
              label="Clients"
              active={typeFilter === 'clients'}
              onClick={() => setTypeFilter('clients')}
              testId="global-search-filter-clients"
              count={results.clients.length}
            />
            <FilterChip
              label="Team"
              active={typeFilter === 'users'}
              onClick={() => setTypeFilter('users')}
              testId="global-search-filter-users"
              count={results.users.length}
            />
            {(typeFilter === 'all' || typeFilter === 'users') && (
              <div className="ml-auto flex items-center gap-1">
                <span className="text-[10px] text-slate-400 mr-1">Role:</span>
                {ROLE_OPTIONS.map((r) => (
                  <FilterChip
                    key={r.value || 'all-roles'}
                    label={r.label}
                    active={roleFilter === r.value}
                    onClick={() => setRoleFilter(r.value)}
                    testId={`global-search-role-${r.value || 'all'}`}
                    size="sm"
                  />
                ))}
              </div>
            )}
          </div>

          {loading ? (
            <div
              className="flex items-center justify-center py-8 text-slate-500 text-sm"
              data-testid="global-search-loading"
            >
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Searching…
            </div>
          ) : q.trim().length < 2 ? (
            <div className="px-4 py-6 text-center text-xs text-slate-500">
              Type at least 2 characters to search
            </div>
          ) : totalVisible === 0 ? (
            <div
              className="px-4 py-8 text-center text-sm text-slate-500"
              data-testid="global-search-no-results"
            >
              No results for{' '}
              <span className="font-semibold text-slate-700">{q}</span>
            </div>
          ) : (
            <div className="py-1">
              {visibleCands.length > 0 && (
                <SectionHeader
                  label="Candidates"
                  count={visibleCands.length}
                  totalUnfiltered={results.candidates.length}
                />
              )}
              {visibleCands.map((c, i) => (
                <ResultRow
                  key={`c-${c.id}`}
                  item={c}
                  active={activeIndex === i}
                  onSelect={() => handleSelect(c)}
                  onMouseEnter={() => setActiveIndex(i)}
                  Icon={User}
                  iconWrapClass="bg-teal-100 text-teal-700"
                  testId={`global-search-result-candidate-${c.id}`}
                />
              ))}

              {visibleJobs.length > 0 && (
                <SectionHeader
                  label="Jobs"
                  count={visibleJobs.length}
                  totalUnfiltered={results.jobs.length}
                />
              )}
              {visibleJobs.map((j, i) => {
                const idx = visibleCands.length + i;
                return (
                  <ResultRow
                    key={`j-${j.id}`}
                    item={j}
                    active={activeIndex === idx}
                    onSelect={() => handleSelect(j)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    Icon={Briefcase}
                    iconWrapClass="bg-indigo-100 text-indigo-700"
                    testId={`global-search-result-job-${j.id}`}
                  />
                );
              })}

              {visibleClients.length > 0 && (
                <SectionHeader
                  label="Clients"
                  count={visibleClients.length}
                  totalUnfiltered={results.clients.length}
                />
              )}
              {visibleClients.map((cl, i) => {
                const idx = visibleCands.length + visibleJobs.length + i;
                return (
                  <ResultRow
                    key={`cl-${cl.id}`}
                    item={cl}
                    active={activeIndex === idx}
                    onSelect={() => handleSelect(cl)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    Icon={Building2}
                    iconWrapClass="bg-rose-100 text-rose-700"
                    testId={`global-search-result-client-${cl.id}`}
                  />
                );
              })}

              {visibleUsers.length > 0 && (
                <SectionHeader
                  label="Team members"
                  count={visibleUsers.length}
                  totalUnfiltered={results.users.length}
                />
              )}
              {visibleUsers.map((u, i) => {
                const idx = visibleCands.length + visibleJobs.length + visibleClients.length + i;
                return (
                  <ResultRow
                    key={`u-${u.id}`}
                    item={u}
                    active={activeIndex === idx}
                    onSelect={() => handleSelect(u)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    Icon={UserCog}
                    iconWrapClass="bg-amber-100 text-amber-700"
                    testId={`global-search-result-user-${u.id}`}
                    showRoleBadge
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FilterChip = ({ label, active, onClick, testId, count, size = 'md' }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    data-active={active ? 'true' : undefined}
    className={`inline-flex items-center gap-1 rounded-full transition ${
      size === 'sm' ? 'text-[10px] px-2 py-0.5' : 'text-xs px-2.5 py-1'
    } ${
      active
        ? 'bg-teal-600 text-white'
        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
    }`}
  >
    {label}
    {typeof count === 'number' && count > 0 ? (
      <span
        className={`rounded-full px-1 py-px ${
          active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'
        }`}
      >
        {count}
      </span>
    ) : null}
  </button>
);

const SectionHeader = ({ label, count, totalUnfiltered }) => (
  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-slate-400 font-semibold flex items-center justify-between">
    <span>{label}</span>
    <span>
      {count}
      {typeof totalUnfiltered === 'number' && totalUnfiltered > count
        ? ` of ${totalUnfiltered}`
        : ''}
    </span>
  </div>
);

const ResultRow = ({ item, active, onSelect, onMouseEnter, Icon, iconWrapClass, testId, showRoleBadge }) => (
  <button
    type="button"
    onClick={onSelect}
    onMouseEnter={onMouseEnter}
    role="option"
    aria-selected={active}
    data-testid={testId}
    data-active={active ? 'true' : undefined}
    className={`w-full text-left flex items-center gap-3 px-3 py-2 transition ${
      active ? 'bg-teal-50' : 'hover:bg-slate-50'
    }`}
  >
    <span
      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${iconWrapClass}`}
    >
      {item.initials || <Icon className="w-4 h-4" />}
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
        {item.name}
        {showRoleBadge && item.role ? (
          <span className="inline-flex items-center px-1.5 py-px rounded bg-amber-100 text-amber-700 text-[9px] font-semibold uppercase tracking-wide">
            {item.role.replace(/_/g, ' ')}
          </span>
        ) : null}
      </div>
      <div className="text-xs text-slate-500 truncate">
        {item.subtitle || item.email || ''}
      </div>
    </div>
  </button>
);

export default GlobalSearch;
